import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TEMPLATE_KINDS, type TemplateKind } from '@/entities/template';

import styles from './Templates.module.css';

/**
 * The category row of Settings -> Templates: Boards | Databases | Notes | Columns | Tasks, always
 * in this order. It scrolls sideways when it does not fit (the page never does), and the active
 * tab scrolls into view. Arrow keys move between tabs.
 */
export function TemplateCategories({
  value,
  onChange,
}: {
  value: TemplateKind;
  onChange: (kind: TemplateKind) => void;
}) {
  const { t } = useTranslation();
  const refs = useRef(new Map<TemplateKind, HTMLButtonElement>());
  const move = (kind: TemplateKind, step: number) => {
    const index = TEMPLATE_KINDS.indexOf(kind);
    const next = TEMPLATE_KINDS[(index + step + TEMPLATE_KINDS.length) % TEMPLATE_KINDS.length];
    if (!next) return;
    onChange(next);
    const node = refs.current.get(next);
    node?.focus();
    // (jsdom has no scrollIntoView)
    if (typeof node?.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  };
  return (
    <div className={styles.tabsScroll}>
      <div role="tablist" aria-label={t('templates.manage.categories')} className={styles.tabs}>
        {TEMPLATE_KINDS.map((kind) => (
          <button
            key={kind}
            ref={(node) => {
              if (node) refs.current.set(kind, node);
              else refs.current.delete(kind);
            }}
            type="button"
            role="tab"
            id={`template-tab-${kind}`}
            aria-selected={kind === value}
            aria-controls="template-panel"
            tabIndex={kind === value ? 0 : -1}
            className={styles.tab}
            data-active={kind === value || undefined}
            onClick={() => {
              onChange(kind);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                move(kind, 1);
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                move(kind, -1);
              }
            }}
          >
            {t(`templates.tabs.${kind}`)}
          </button>
        ))}
      </div>
    </div>
  );
}
